import { apiClient } from "@/api/api-client";
import { useAuth } from "@/hooks/use-auth";
import ReactECharts from "echarts-for-react";
import { useEffect, useRef, useState } from "react";

const colors = {
	pending: "#f87171",
	withDecision: "#eab308",
	withAppeal: "#3b82f6",
	caseClosed: "#82ca9d",
};

const CaseCategoryChart = ({ hasBranch = false }: { hasBranch: boolean }) => {
	const myChart = useRef();
	const { user } = useAuth();

	const staticOptions = {
		tooltip: {
			trigger: "axis",
			axisPointer: {
				type: "shadow",
			},
		},
		legend: {
			right: 0,
			top: 10,
		},
		grid: {
			left: "3%",
			right: "4%",
			bottom: "3%",
			containLabel: true,
		},
	};

	const [statistics, setStatistics] = useState<any>({});

	const fetchStatistics = async () => {
		const ids = hasBranch ? user?.branches?.map((branch) => branch?.assignable?.id) : "";
		const response = await apiClient.get(`/backoffice/case-category-report?branch_id=${ids}`);

		if (response && response.data) {
			const item = response.data;
			setStatistics({
				...staticOptions,

				title: {
					text: "",
					top: 10,
					left: 0,
					textStyle: {
						fontSize: 14,
					},
				},
				xAxis: {
					type: "value",
					boundaryGap: [0, 0.01],
					minInterval: 1,
					// max: item.total_count,
				},
				yAxis: {
					type: "category",
					inverse: true,
					data: item.case_categories,
				},
				series: [
					{
						name: "Pending w/o Decision",
						type: "bar",
						data: item.pending_count,
						itemStyle: {
							color: colors.pending,
						},
					},
					{
						name: "Pending w/ Decision",
						type: "bar",
						data: item.with_decision_count,
						itemStyle: {
							color: colors.withDecision,
						},
					},
					{
						name: "With Appeal",
						type: "bar",
						data: item.with_appeal_count,
						itemStyle: {
							color: colors.withAppeal,
						},
					},
					{
						name: "Case Closed",
						type: "bar",
						data: item.case_closed_count,
						itemStyle: {
							color: colors.caseClosed,
						},
					},
				],
				graphic: [
					{
						type: "text",
						left: 5,
						bottom: 10,
						style: {
							text: "",
							fontSize: 14,
							backgroundColor: "none",
							// height: 34,
						},
						onclick: () => {},
					},
				],
				type_data: item.type_data,
			});
		}
	};

	const goBack = (index: number) => {
		myChart.current?.getEchartsInstance()?.setOption(statistics);
	};

	const goToCaseCategory = (index) => {
		myChart.current?.getEchartsInstance()?.setOption({
			...staticOptions,
			tooltip: { extraCssText: "width: 300px; white-space:pre-wrap;" },
			grid: {
				left: "400",
				right: "4%",
				bottom: "3%",
				containLabel: false,
			},
			title: {
				text: statistics.yAxis.data[index],
				top: 10,
				left: 0,
				textStyle: {
					fontSize: 14,
				},
			},
			legend: {
				right: 0,
				top: 10,
			},
			xAxis: {
				type: "value",
				boundaryGap: [0, 0.01],
				minInterval: 1,
			},
			yAxis: {
				type: "category",
				data: statistics.type_data[index].type_names,
				axisLabel: {
					overflow: "truncate",
					width: 300,
				},
			},
			series: [
				{
					name: "Pending w/o Decision",
					type: "bar",
					data: statistics.type_data[index].pending_count,
					itemStyle: {
						color: colors.pending,
					},
				},
				{
					name: "Pending w/ Decision",
					type: "bar",
					data: statistics.type_data[index].with_decision_count,
					itemStyle: {
						color: colors.withDecision,
					},
				},
				{
					name: "With Appeal",
					type: "bar",
					data: statistics.type_data[index].with_appeal_count,
					itemStyle: {
						color: colors.withAppeal,
					},
				},
				{
					name: "Case Closed",
					type: "bar",
					data: statistics.type_data[index].case_closed_count,
					itemStyle: {
						color: colors.caseClosed,
					},
				},
			],
			graphic: [
				{
					type: "text",
					left: 5,
					bottom: 10,
					style: {
						text: "Back",
						fontSize: 14,
						// height: 34,
						fontWeight: "600",
						fill: "#fff",
						textVerticalAlign: "center",
						padding: [12, 12, 12, 12],
						borderRadius: 6,
						backgroundColor: "hsla(279, 42%, 45%)",
					},
					onclick: () => {
						goBack(index);
					},
				},
			],
		});
	};

	useEffect(() => {
		fetchStatistics();
	}, []);

	return (
		<div className="grid grid-cols-12 w-full gap-4">
			{statistics && (
				<div className="col-span-12">
					<div className="flex flex-col w-full gap-4">
						<div className="flex flex-col w-full h-auto rounded-md border border-border/50 overflow-hidden">
							<div className="flex flex-row w-full p-3 border-b border-border/50 items-center">
								<div className="flex flex-col w-6/12 text-base font-semibold text-foreground-dark">Cases Per Category</div>
								<div className="flex text-right flex-col w-6/12 text-base font-semibold text-foreground-dark">{/* Total Cases: */}</div>
							</div>

							<div className="w-full h-full shrink-0 px-2">
								<div
									style={{
										width: "100%",
										height: 700,
									}}
								>
									<ReactECharts
										option={statistics}
										ref={myChart}
										style={{ height: "100%", width: "100%" }}
										onEvents={{
											click: (event: any) => {
												console.log("clicked", event);
												if (event.componentType !== "graphic") {
													goToCaseCategory(event.dataIndex);
												} else {
													// goToCaseTypes(index);
												}
											},
										}}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CaseCategoryChart;
